// V2 版式：bar-chart（前端 DOM 版）——对照后端 _shared/pptx/v2/layouts/bar-chart.ts 逐坐标移植
// 5–8 项横向条形：左标签列、条形按 value 比例、末端数值标签（fontMono）；
// accent 只给第一名（最大值）条形，其余灰阶。
// 几何取 V2GEO["bar-chart"]（deno dump 同源，勿手改数值）。

import { SPACING } from "../../lib/tokens.generated";
import { V2GEO } from "../../lib/v2geo.generated";
import { px, pt, titleSize, kickerOf, footOf } from "./shared";
import { luxeExt } from "./luxe";
import { termTheme, inkTheme, memphisTheme, memphisPalette } from "./themes";
import V2Chrome from "./Chrome";
import { EditableText } from "../SlideCanvas";
import type { V2LayoutProps } from "./V2Slide";

// deno dump 同源：与后端 bar-chart.ts GEO 一一对应
const GEO = V2GEO["bar-chart"] as any;

// chart block 结构以后端 _shared/pptx/types.ts 为准（前端 slides.ts Block 同步后此处收窄仍成立）
type ChartItem = { label: string; value: number; unit?: string };
type ChartBlock = { type: "chart"; items: ChartItem[] };

export default function V2BarChart({ c, skin, page, total, editable, handlers }: V2LayoutProps) {
  const m = SPACING.marginX;
  const W = 13.333;
  const mag = skin.style === "magazine";
  // luxe：首名条形裸金，其余浅金 blush（bar-chart.ts 分支）
  const ext = luxeExt(skin);
  // ink：首名条墨黑（朱红只给首名数值）；memphis：5 色轮用 + 胶囊条；term：首条磷光绿描边
  const inkT = inkTheme(skin);
  const mpT = memphisTheme(skin);
  const termT = termTheme(skin);

  const ci = c.blocks.findIndex((b) => (b as { type: string }).type === "chart");
  const chart = ci >= 0 ? (c.blocks[ci] as unknown as ChartBlock) : null;
  const items = chart ? chart.items.slice(0, 8) : [];

  const n = items.length;
  const chartH = GEO.chart.bottom - GEO.chart.top;
  const rowH = n > 0 ? (chartH - (n - 1) * GEO.rowGap) / n : 0;
  const barH = rowH * GEO.barHRatio;

  const barX = m + GEO.labelW + GEO.labelGap;
  const maxBarW = W - m - GEO.valueW - GEO.valueGap - barX;
  // 045 #12：编辑会把字符串存进 number 字段——比较/比例一律先 Number() 归一
  const numOf = (v: number | string): number => {
    const x = typeof v === "number" ? v : Number(v);
    return Number.isFinite(x) ? x : 0;
  };
  const maxV = Math.max(...items.map((it) => numOf(it.value)), 0);

  return (
    <>
      <V2Chrome skin={skin} kicker={kickerOf(page, "bar-chart")} page={page} total={total} foot={footOf()} />

      {/* 页面标题（长度分档） */}
      <EditableText
        value={c.title} path="title" editable={editable} multiline
        onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
        style={{
          position: "absolute", left: px(GEO.title.x), top: px(GEO.title.y),
          width: px(GEO.title.w), height: px(GEO.title.h),
          fontSize: pt(titleSize(c.title, skin.scale.pageTitle)), color: skin.text,
          fontFamily: skin.fontHead, fontWeight: skin.serifHead ? 700 : 400, lineHeight: 1.15,
        }}
      />

      {items.map((it, i) => {
        const rowY = GEO.chart.top + i * (rowH + GEO.rowGap);
        const v = numOf(it.value);
        const isFirst = v === maxV && maxV > 0;
        const ratio = maxV > 0 ? v / maxV : 0;
        const barW = Math.max(maxBarW * ratio, maxBarW * GEO.minBarRatio);
        // 数值标签：value 为 number，带 unit 显示（同后端拼法）
        const val = it.unit ? `${it.value}${it.unit}` : String(it.value);
        // 045 #12：持久化前 parse 回 number（先剔展示用 unit/千分位；parse 失败保留原文防毁数据）
        const onEditValue = (p: string, text: string) => {
          const stripped = (it.unit && text.endsWith(it.unit)) ? text.slice(0, -it.unit.length) : text;
          const num = Number(stripped.replace(/[,\s]/g, ""));
          handlers.onEdit?.(p, stripped.trim() !== "" && Number.isFinite(num) ? num : text);
        };
        return (
          <div key={i}>
            {/* 左标签列（valign middle） */}
            <EditableText
              value={it.label} path={`blocks.${ci}.items.${i}.label`} editable={editable}
              onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
              style={{
                position: "absolute", left: px(m), top: px(rowY),
                width: px(GEO.labelW), height: px(rowH),
                fontSize: pt(skin.scale.body), color: inkT ? inkT.ink : isFirst ? skin.accent : skin.text,
                fontFamily: skin.fontBody, fontWeight: isFirst ? 700 : 400,
                display: "flex", alignItems: "center",
              }}
            />
            {/* 条形：按 value 比例，垂直居中于行；memphis 胶囊条 5 色轮用；term 首条近黑 + 磷光绿描边（bar-chart.ts:77-89） */}
            <div style={{
              position: "absolute", left: px(barX), top: px(rowY + (rowH - barH) / 2),
              width: px(barW), height: px(barH),
              background: mpT
                ? memphisPalette(mpT)[(page - 1 + i) % 5]
                : inkT ? (isFirst ? inkT.ink : inkT.accentGrey)
                : isFirst ? (termT ? termT.paper : skin.accent)
                : ext ? ext.blush : skin.hairline,
              border: termT && isFirst ? `1.25px solid ${skin.accent}` : undefined,
              borderRadius: mpT ? px(0.1) : mag ? px(skin.radius) : 0,
            }} />
            {/* 末端数值标签（fontMono） */}
            <EditableText
              value={val} path={`blocks.${ci}.items.${i}.value`} editable={editable}
              onEdit={onEditValue} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
              style={{
                position: "absolute", left: px(barX + barW + GEO.valueGap), top: px(rowY),
                width: px(GEO.valueW), height: px(rowH),
                fontSize: pt(skin.scale.body), color: inkT ? (isFirst ? inkT.accent : inkT.muted) : isFirst ? skin.accent : skin.sub,
                fontFamily: skin.fontMono, fontWeight: isFirst ? 700 : 400,
                display: "flex", alignItems: "center", whiteSpace: "nowrap",
              }}
            />
          </div>
        );
      })}
    </>
  );
}
